import { useCallback, useEffect, useId, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Loader2, CornerDownLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { PriceBasisBadge } from "@/components/PriceBasisBadge";
import { api, type CardsListResp, type CardSummary } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { cn } from "@/lib/cn";

const MIN_CHARS = 3;
const MAX_SUGGESTIONS = 8;

/**
 * Recherche globale avec autocomplétion.
 * - propose des cartes dès {@link MIN_CHARS} caractères saisis (debounce + /api/cards) ;
 * - navigation clavier (↑/↓), validation par « Entrée », fermeture par « Échap » ;
 * - sélectionner une carte → /?card=<ref> : l'accueil affiche le détail inline.
 *
 * Présent dans le TopNav (variant="nav", partout) et le hero de l'accueil
 * (variant="hero").
 */
export function GlobalSearch({ variant = "nav" }: { variant?: "nav" | "hero" }) {
  const navigate = useNavigate();
  const { t, fmtMoney } = useI18n();
  const listboxId = useId();

  const [query, setQuery] = useState("");
  const [debounced, setDebounced] = useState("");
  const [items, setItems] = useState<CardSummary[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);

  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce de la saisie.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(query.trim()), 200);
    return () => clearTimeout(id);
  }, [query]);

  // Fetch des suggestions dès MIN_CHARS, annulable.
  useEffect(() => {
    if (debounced.length < MIN_CHARS) {
      setItems([]);
      setLoading(false);
      return;
    }
    let cancelled = false;
    const ctrl = new AbortController();
    setLoading(true);
    api<CardsListResp>(
      `/api/cards?search=${encodeURIComponent(debounced)}&limit=${MAX_SUGGESTIONS}&page=1`,
      { signal: ctrl.signal },
    )
      .then((r) => {
        if (cancelled) return;
        setItems(r.items ?? []);
        setActive(0);
        setOpen(true);
      })
      .catch((e) => {
        if (cancelled || (e as Error).name === "AbortError") return;
        setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
      ctrl.abort();
    };
  }, [debounced]);

  // Fermeture au clic extérieur.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const select = useCallback(
    (card: CardSummary | undefined) => {
      if (!card) return;
      setOpen(false);
      inputRef.current?.blur();
      navigate(`/?card=${encodeURIComponent(card.id)}`);
    },
    [navigate],
  );

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      if (items.length) {
        setOpen(true);
        setActive((i) => (i + 1) % items.length);
      }
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      if (items.length) {
        setOpen(true);
        setActive((i) => (i - 1 + items.length) % items.length);
      }
    } else if (e.key === "Enter") {
      e.preventDefault();
      // Validation : la suggestion active, sinon la première proposition.
      select(items[active] ?? items[0]);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  const showHint = query.trim().length > 0 && query.trim().length < MIN_CHARS;
  const showDropdown = open && debounced.length >= MIN_CHARS;

  return (
    <div ref={rootRef} className={cn("relative", variant === "hero" ? "w-full" : "w-full sm:w-72")}>
      <Input
        ref={inputRef}
        leftIcon={<Search />}
        className={variant === "hero" ? "h-11 text-base" : undefined}
        placeholder={t("search.placeholder") || "Search a card — EN_ASR_001 or name…"}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={onKeyDown}
        onFocus={() => debounced.length >= MIN_CHARS && setOpen(true)}
        role="combobox"
        aria-expanded={showDropdown}
        aria-controls={listboxId}
        aria-autocomplete="list"
        rightSlot={loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
      />

      {showHint ? (
        <div className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] text-muted-fg">
          {MIN_CHARS}+ {t("search.min_chars") || "chars"}
        </div>
      ) : null}

      {showDropdown ? (
        <div
          id={listboxId}
          role="listbox"
          className="absolute left-0 right-0 top-[calc(100%+0.35rem)] z-50 overflow-hidden rounded-md border border-border bg-bg shadow-lg"
        >
          {items.length === 0 ? (
            <div className="px-3 py-3 text-xs text-muted-fg">
              {loading ? t("search.loading") || "Searching…" : t("search.no_match") || "No card matches."}
            </div>
          ) : (
            <ul className="max-h-80 overflow-auto py-1">
              {items.map((card, i) => (
                <li key={card.id} role="option" aria-selected={i === active}>
                  <button
                    type="button"
                    // onMouseDown plutôt que onClick : se déclenche avant le blur de l'input.
                    onMouseDown={(e) => {
                      e.preventDefault();
                      select(card);
                    }}
                    onMouseEnter={() => setActive(i)}
                    className={cn(
                      "flex w-full items-center gap-3 px-3 py-2 text-left transition-colors",
                      i === active ? "bg-surface-2" : "hover:bg-surface-2/60",
                    )}
                  >
                    <span className="shrink-0 font-mono text-xs text-primary">{card.id}</span>
                    <span className="min-w-0 flex-1 truncate text-sm font-medium">
                      {card.match === "approx" ? (
                        <span className="mr-1 text-[10px] font-normal italic text-muted-fg">
                          {t("search.approx") || "proche de"}
                        </span>
                      ) : null}
                      {card.name ?? "—"}
                    </span>
                    <span className="hidden shrink-0 text-xs text-muted-fg sm:inline">
                      {card.set_name ?? card.set_code ?? ""}
                    </span>
                    {card.rarity ? (
                      <Badge tone="info" variant="soft" className="hidden shrink-0 md:inline-flex">
                        {card.rarity}
                      </Badge>
                    ) : null}
                    <span className="shrink-0 font-mono text-xs tabular-nums text-muted-fg">
                      {card.price != null ? fmtMoney(card.price) : ""}
                    </span>
                    {card.price != null ? <PriceBasisBadge basis={card.price_basis} compact className="shrink-0" /> : null}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <div className="flex items-center justify-between border-t border-border px-3 py-1.5 text-[10px] text-muted-fg">
            <span className="inline-flex items-center gap-1">
              <CornerDownLeft className="h-3 w-3" /> {t("search.enter_hint") || "Enter to open"}
            </span>
            <span>↑ ↓ {t("search.nav_hint") || "to navigate"}</span>
          </div>
        </div>
      ) : null}
    </div>
  );
}
